import React, { useRef, useEffect } from "react";
import styled from 'styled-components';
import { BoldTextColor, PrimaryTextColor, BlueText } from '../../../app/theme/tokens/colors';
import { InputText } from '../../../shared/ui/Inputs';
import { ErrorMessage } from '../../../shared/messages/messages';
import "altcha";
import "altcha/i18n/pt-br";
const altchaUrl = process.env.REACT_APP_WM_ANTIFRAUD_URL;

export const CardWrapper = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 40px 40px 32px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const LogoLogin = styled.div`
  width: 260px;
  height: 90px;
  margin: 0 auto 16px auto;
  background-image: url(/icones/logo-color.png);
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-width: 600px) {
    width: 220px;
    height: 70px;
  }
`;

export const PBemVindo = styled.p`
  color: ${BoldTextColor};
  font-size: 22px;
  text-align: center;
  margin: 0 0 4px 0;
`;

export const PAcessarSuaConta = styled.p`
  color: ${PrimaryTextColor};
  font-size: 16px;
  text-align: center;
  margin: 0 0 24px 0;
`;

export const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

export const InputCode = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const InputFull = styled(InputText)`
  width: 100%;
`;

export const PasswordFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const PasswordToggleButton = styled.button`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${PrimaryTextColor};
`;

export const ActionsWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  & > button {
    width: 100%;
  }
`;

export const RecaptchaWrapper = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: center;
`;

export const CustomErrorMessage = styled(ErrorMessage)`
  width: 100%;
  margin: 0 0 12px 0;
`;

export const OptionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  width: 100%;
`;

export const KeepConnectedGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${PrimaryTextColor};
  cursor: pointer;
`;

export const ForgotPasswordLink = styled.span`
  color: ${BlueText};
  cursor: pointer;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

export const PWithFontSize = styled.p`
  font-size: ${(props: { fontSize?: string }) => props.fontSize || '14px'};
`;

const BackRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  margin-bottom: 16px;
  border: none;
  background: transparent;
  color: ${PrimaryTextColor};
  cursor: pointer;
  font-size: 14px;
  padding: 4px 0;

  svg {
    font-size: 16px;
  }

  @media (max-width: 600px) {
    margin-bottom: 8px;
  }
`;

type AuthCardProps = {
    title?: string;
    subtitle?: string;
    message?: string;
    showBack?: boolean;
    onBack?: () => void;
    children: React.ReactNode;
};

export const AuthCard: React.FC<AuthCardProps> = ({ title, subtitle, message, showBack, onBack, children }) => {
    return (
        <CardWrapper>
            {showBack && onBack && (
                <BackRow type="button" onClick={onBack}>
                    <span>← Voltar</span>
                </BackRow>
            )}

            <LogoLogin />

            {title && (
                <PBemVindo>
                    {title} <b>Portal Homecare</b>
                </PBemVindo>
            )}
            {subtitle && <PAcessarSuaConta>{subtitle}</PAcessarSuaConta>}

            {message && <CustomErrorMessage>{message}</CustomErrorMessage>}

            <FormContent>{children}</FormContent>
        </CardWrapper>
    );
};

type RecaptchaBoxProps = {
    isReady: boolean;                     // recebido de fora
    onChangeReady?: (v: boolean) => void; // informa para fora
    onChangePayload?: (p: string | null) => void;
};
export const RecaptchaBox: React.FC<RecaptchaBoxProps> = ({
    onChangeReady,
    onChangePayload
}) => {
    const widgetRef = useRef<any>(null);

    useEffect(() => {
        const el = widgetRef.current;
        if (!el) return;

        const setNotReady = () => {
            onChangePayload?.(null);
            onChangeReady?.(false);
        };

        const handleState = (e: any) => {
            const state = e?.detail?.state;
            if (state === 'running' || state === 'idle' || state === 'error') {
                setNotReady();
            }
        };

        const handleVerify = (e: any) => {
            const payload = e?.detail?.payload ?? null;
            if (payload) {
                onChangePayload?.(payload);
                onChangeReady?.(true);
            } else {
                setNotReady();
            }
        };

        const handleError = () => {
            setNotReady();
        };

        el.addEventListener('statechange', handleState);
        el.addEventListener('verified', handleVerify);
        el.addEventListener('error', handleError);

        return () => {
            el.removeEventListener('statechange', handleState);
            el.removeEventListener('verified', handleVerify);
            el.removeEventListener('error', handleError);
        };
    }, [onChangePayload, onChangeReady]);

    return (
        <RecaptchaWrapper>
            <altcha-widget
                ref={widgetRef}
                challengeurl={altchaUrl}
                language="pt-br"
                hidefooter
                hidelogo
                style={{
                    ['--altcha-color-base' as any]: 'transparent',
                    ['--altcha-color-text' as any]: '#000000',
                    ['--altcha-color-border' as any]: '#CBD5E1',
                    ['--altcha-color-border-focus' as any]: '#333333',
                    ['--altcha-color-error-text' as any]: '#DC2626',
                    ['--altcha-color-footer-bg' as any]: '#F8FAFC',
                    ['--altcha-border-radius' as any]: '10px',
                    ['--altcha-border-width' as any]: '1px',
                    ['--altcha-max-width' as any]: '420px',
                    width: 'calc(100% - 20px)',
                    padding: '10px'
                }}
            />
        </RecaptchaWrapper>
    );
};

export const isValidMail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); // TODO: organizar em uma lib
}
